import React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ITelemetry } from '../telemetry';
import { IComponent } from './qos/types';
import { TelemetryContext } from './TelemetryContext';

interface ITelemetryProviderProps {
  telemetry: ITelemetry;
}

/**
 * The context provider that passes the telemetry instance to the context so it can be consumed by all the child components
 */
export const TelemetryProvider: React.FC<ITelemetryProviderProps> = ({ children, telemetry: telemetryParam }) => {
  const isFmpReported = useRef(false);
  const [componentCount, setComponentCount] = useState<number | null>(null);
  const firstMeaningfulContent = useRef<Set<IComponent>>(new Set<IComponent>());
  const [telemetry, setTelemetry] = useState<ITelemetry>(telemetryParam);

  useEffect(() => {
    if (!isFmpReported.current && componentCount === 0) {
      isFmpReported.current = true;
      telemetry.reportPerformance('FMP', {
        fmp: Date.now() - performance.timing?.navigationStart
      });
    }
  }, [telemetry, componentCount]);

  const registerToFirstMeaningfulContent = useCallback((component: IComponent) => {
    firstMeaningfulContent.current.add(component);
    setComponentCount(firstMeaningfulContent.current.size);
  }, []);

  const unRegisterToFirstMeaningfulContent = useCallback((component: IComponent) => {
    if (firstMeaningfulContent.current.size > 0) {
      firstMeaningfulContent.current.delete(component);
      setComponentCount(firstMeaningfulContent.current.size);
    }
  }, []);

  const telemetryContextValue = useMemo(() => {
    return {
      telemetry,
      registerToFirstMeaningfulContent,
      unRegisterToFirstMeaningfulContent,
      setTelemetry
    };
  }, [telemetry, registerToFirstMeaningfulContent, unRegisterToFirstMeaningfulContent]);

  return <TelemetryContext.Provider value={telemetryContextValue}>{children}</TelemetryContext.Provider>;
};
